import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { configAPI, publicAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { User, Phone, MapPin, MessageSquare, Tv, Wifi, Send, CheckCircle2 } from 'lucide-react';

const SERVICE_OPTIONS = [
  { value: 'catv', label: 'CableTV', icon: Tv },
  { value: 'broadband', label: 'Broadband', icon: Wifi },
  { value: 'both', label: 'Both', icon: null },
];

export default function NewConnectionRequestPage() {
  const [form, setForm] = useState({ name: '', mobile: '', address: '', note: '', connectionType: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [brand, setBrand] = useState({ name: 'AngelNet', logo: '', phone: '' });

  useEffect(() => {
    configAPI.get().then(({ data }) => {
      const d = data?.data;
      if (!d) return;
      const identity = d.catv?.name ? d.catv : d.broadband?.name ? d.broadband : null;
      setBrand({
        name: identity?.name || d.companyName || 'AngelNet',
        logo: identity?.logo || '',
        phone: identity?.phone || d.companyPhone || '',
      });
    }).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.connectionType) { toast.error('Please select which service you\'re interested in'); return; }
    setLoading(true);
    try {
      await publicAPI.submitConnectionRequest(form);
      setSubmitted(true);
      setForm({ name: '', mobile: '', address: '', note: '', connectionType: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not submit your request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-split">
      <style>{`
        .login-split { min-height: 100vh; display: flex; flex-direction: column; }
        .login-split-left, .login-split-right { display: flex; flex-direction: column; align-items: center; }
        .login-split-left { padding: 48px 20px 34px; }
        .login-split-right { padding: 32px 20px calc(24px + env(safe-area-inset-bottom, 0px)); background: #f8fafc; flex: 1; }
        @media (min-width: 900px) {
          .login-split { flex-direction: row; }
          .login-split-left { flex: 0 0 44%; justify-content: center; padding: 60px 40px; }
          .login-split-right { justify-content: center; padding: 40px; }
        }
      `}</style>

      {/* Left — brand panel */}
      <div className="login-split-left" style={{
        background: 'radial-gradient(circle at 50% 0%, #1e3a8a 0%, #0c1a3d 55%, #060e24 100%)',
      }}>
        <div style={{
          width: 90, height: 90, borderRadius: '50%', overflow: 'hidden',
          border: '3px solid rgba(255,255,255,.35)', boxShadow: '0 8px 28px rgba(0,0,0,.35)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0c1a3d', flexShrink: 0,
        }}>
          {brand.logo ? (
            <img src={brand.logo} alt={brand.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <Tv size={36} color="#fff" />
          )}
        </div>

        <h1 style={{
          color: '#fff', fontSize: '1.8rem', fontWeight: 900, letterSpacing: '.03em',
          margin: '16px 0 8px', textAlign: 'center', textTransform: 'uppercase',
        }}>
          {brand.name}
        </h1>
        <p style={{ color: 'rgba(255,255,255,.65)', fontSize: '.9rem', textAlign: 'center', maxWidth: 320 }}>
          Get connected in a few simple steps — tell us where, and we'll take care of the rest.
        </p>
      </div>

      {/* Right — form card */}
      <div className="login-split-right">
        <div style={{
          width: '100%', maxWidth: 380, background: '#fff', borderRadius: 20,
          padding: '30px 26px', boxShadow: '0 8px 30px rgba(0,0,0,.08)', border: '1px solid #e5e7eb',
        }}>
          {submitted ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <CheckCircle2 size={48} color="var(--primary)" style={{ marginBottom: 14 }} />
              <h2 style={{ fontSize: '1.25rem', fontWeight: 800, marginBottom: 8, color: '#111827' }}>Request Received!</h2>
              <p style={{ fontSize: '.88rem', color: '#6b7280', marginBottom: 22 }}>
                Thanks for your interest. Our team will contact you shortly to set up your new connection.
              </p>
              <Link to="/portal/login" style={{ fontSize: '.85rem', color: 'var(--primary)', fontWeight: 600, textDecoration: 'none' }}>
                ← Back to Login
              </Link>
            </div>
          ) : (
            <>
              <h2 style={{ fontSize: '1.4rem', fontWeight: 800, marginBottom: 2, color: '#111827' }}>
                Get a New <span style={{ color: 'var(--primary)' }}>Connection</span>
              </h2>
              <p style={{ fontSize: '.85rem', color: '#6b7280', marginBottom: 4 }}>Tell us a bit about yourself and we'll reach out</p>
              <div style={{ width: 40, height: 3, background: 'var(--primary)', borderRadius: 2, marginBottom: 22 }} />

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label className="form-label">Full Name</label>
                  <div style={{ position: 'relative' }}>
                    <User size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                    <input
                      type="text" className="form-control" placeholder="Your name"
                      value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                      style={{ paddingLeft: 36 }} required autoFocus
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Mobile Number</label>
                  <div style={{ position: 'relative' }}>
                    <Phone size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                    <input
                      type="tel" className="form-control" placeholder="Your mobile number"
                      value={form.mobile} onChange={e => setForm({ ...form, mobile: e.target.value })}
                      style={{ paddingLeft: 36 }} required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Address</label>
                  <div style={{ position: 'relative' }}>
                    <MapPin size={15} style={{ position: 'absolute', left: 12, top: 12, color: 'var(--text-muted)' }} />
                    <textarea
                      className="form-control" placeholder="Where should we install your connection?"
                      value={form.address} onChange={e => setForm({ ...form, address: e.target.value })}
                      style={{ paddingLeft: 36, minHeight: 60, resize: 'vertical' }}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Which service are you interested in? *</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {SERVICE_OPTIONS.map(({ value, label, icon: Icon }) => (
                      <button
                        key={value} type="button"
                        onClick={() => setForm(f => ({ ...f, connectionType: value }))}
                        style={{
                          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                          padding: '10px 8px', borderRadius: 10, fontSize: '.82rem', fontWeight: 600, cursor: 'pointer',
                          border: form.connectionType === value ? '1.5px solid var(--primary)' : '1px solid #e5e7eb',
                          background: form.connectionType === value ? 'rgba(37,99,235,.08)' : '#fff',
                          color: form.connectionType === value ? 'var(--primary)' : '#374151',
                        }}
                      >
                        {Icon && <Icon size={14} />} {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Note (optional)</label>
                  <div style={{ position: 'relative' }}>
                    <MessageSquare size={15} style={{ position: 'absolute', left: 12, top: 12, color: 'var(--text-muted)' }} />
                    <textarea
                      className="form-control" placeholder="Anything else we should know?"
                      value={form.note} onChange={e => setForm({ ...form, note: e.target.value })}
                      style={{ paddingLeft: 36, minHeight: 50, resize: 'vertical' }}
                    />
                  </div>
                </div>

                <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', gap: 8, padding: '12px', fontSize: '.92rem', marginTop: 10 }} disabled={loading}>
                  <Send size={16} /> {loading ? 'Submitting…' : 'Submit Request'}
                </button>
              </form>

              <div style={{ marginTop: 18, textAlign: 'center' }}>
                <Link to="/portal/login" style={{ fontSize: '.8rem', color: 'var(--text-muted)', textDecoration: 'none' }}>
                  ← Already a customer? Login
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
